import isNull from 'lodash/isNull';
import { FormattedMessage } from 'react-intl';

import { get } from 'utils/lodash';
import { getOnetimeLabel } from 'utils/transaction';

import { CURRENCY_IDR, DEFAULT_INT_ZERO, MULTICURRENCY_PRODUCT_TYPE } from 'settings/constants/common';
import { DEFAULT_MIN_TRANSFER_AMOUNT, DEFAULT_MAX_TRANSFER_AMOUNT, FIELD_TYPE_TEXT } from 'settings/constants/transaction';
import { formatArrayKeys } from 'utils/formatter';
import { getEWalletDetail, getEWalletFavouriteDetail } from 'middleware/actions/transfer';

export const getEWalletBeneficiaryDetails = ({ confirmationDetail, referenceNumber, isResultSuccess }, isFavouriteTransfer) => {
  const dateLabel = getOnetimeLabel(confirmationDetail);
  const payeeNameInfo = {
    title: <FormattedMessage id="transaction.transfer.eWallet.eWalletName" />,
    info: get(confirmationDetail, 'payeeName', ''),
  };
  const adminFeeInfo = {
    title: <FormattedMessage id="transaction.transfer.eWallet.adminFee" />,
    info: get(confirmationDetail, 'adminFeeDisplay', ''),
  };

  const details = [
    ...(!isFavouriteTransfer ? [payeeNameInfo] : []),
    {
      title: <FormattedMessage id="transaction.transfer.effectiveDate" />,
      info: (
        <span>
          {dateLabel}
          <span className="gray pl1">{get(confirmationDetail, 'effectiveDateDisplay', '')}</span>
        </span>
      ),
    },
    ...(isFavouriteTransfer ? [adminFeeInfo] : []),
  ];

  return isResultSuccess
    ? details.concat({ title: <FormattedMessage id="transaction.transfer.referenceNumber" />, info: referenceNumber })
    : details;
};

export const getEWalletMonetaryDetails = confirmationDetail => [
  {
    title: <FormattedMessage id="transaction.transfer.transactionAmount" />,
    info: get(confirmationDetail, 'toAmountDisplay', ''),
  },
  {
    title: <FormattedMessage id="transaction.transfer.transactionFee" />,
    info: get(confirmationDetail, 'adminFeeDisplay', ''),
  },
  {
    title: <FormattedMessage id="transaction.transfer.totalDebit" />,
    info: get(confirmationDetail, 'totalPaymentDisplay', ''),
  },
  {
    title: <FormattedMessage id="transaction.transfer.localDebitedAmount" />,
    info: get(confirmationDetail, 'equivalentIdrAmountDisplay', ''),
  },
];
export const getEWalletDynamicFields = (detail, senderAccount, isFavouriteTransfer) => {
  const minAmount = isFavouriteTransfer ? get(detail, 'minLimit', '') : get(detail, 'beneficiaryBank.minLimit', DEFAULT_INT_ZERO);
  const maxAmount = isFavouriteTransfer ? get(detail, 'maxLimit', '') : get(detail, 'beneficiaryBank.maxLimit', DEFAULT_INT_ZERO);
  const currentBalance = get(senderAccount, 'plainBalance', DEFAULT_INT_ZERO);
  const currency = get(senderAccount, 'currency', CURRENCY_IDR);

  return [
    {
      name: 'accountNumber',
      readOnly: isFavouriteTransfer,
    },
    {
      name: 'transferAmount',
      validationRules: [
        { type: 'required', isValidateRequired: true },
        { type: 'minAmount', value: minAmount > DEFAULT_INT_ZERO ? minAmount : DEFAULT_MIN_TRANSFER_AMOUNT, isValidateRequired: true },
        {
          type: 'maxAmount',
          value: maxAmount > DEFAULT_MIN_TRANSFER_AMOUNT ? maxAmount : DEFAULT_MAX_TRANSFER_AMOUNT,
          isValidateRequired: true,
        },
        {
          type: 'senderBalance',
          value: currentBalance,
          isValidateRequired: currency === CURRENCY_IDR, // is same currency? yes : no;
        },
      ],
    },
  ];
};

export const getEWalletFavouriteDynamicFields = (detail, isFromTransferResult) => {
  const payees = get(detail, 'emoneyBillerMap', []);
  const accountHolderName = get(detail, 'accountHolderName', '');

  return isFromTransferResult
    ? [
        {
          isHidden: false,
          name: 'accountHolderName',
        },
        {
          readOnly: true,
          name: 'payeeCode',
          type: FIELD_TYPE_TEXT,
        },
      ]
    : [
        {
          options: formatArrayKeys(payees),
          name: 'payeeCode',
        },
        {
          name: 'accountHolderName',
          isHidden: !accountHolderName,
        },
      ];
};

export const getEWalletFavouriteTransferDetail = (detail, isEditFavourite) => {
  const payeeCode = get(detail, 'payeeCode', '');
  const payeeName = get(detail, 'payeeName', '');
  const toAccountNo = get(detail, 'toAccountNo', '');
  const accountNickname = get(detail, 'accountNickname', '');
  const accountHolderName = get(detail, 'accountHolderName', '');
  const accountLimit = get(detail, 'accountLimit', '');

  return { payeeCode, payeeName, toAccountNo, accountHolderName, accountLimit, ...(isEditFavourite && { accountNickname }) };
};

export const getEWalletFavouriteBeneficiaryDetails = detail => [
  {
    title: <FormattedMessage id="transaction.favourite.form.beneficiaryNickname" />,
    info: get(detail, 'accountNickname', '-'),
  },
];

export const getEWalletCommonFavouriteResultPayload = detail => ({
  userId: get(detail, 'userId', ''),
  tacValue: get(detail, 'tacValue', ''),
  restSMSService: get(detail, 'tacDetail.tacViewBean.restSMSService', {}),
  accountNickname: get(detail, 'confirmationDetail.accountNickname', ''),
  toAccountNo: get(detail, 'confirmationDetail.toAccountNo', ''),
  accountLimit: get(detail, 'confirmationDetail.accountLimit', ''),
  payeeCode: get(detail, 'confirmationDetail.payeeCode', ''),
  payeeName: get(detail, 'confirmationDetail.payeeName', ''),
});

export const getEWalletDetailDispatcher = ({ userId, favouriteIndex, favouriteAccount, isSenderIDR, productCode, dispatch }) => {
  const favAccountMaps = get(favouriteAccount, 'favouriteAccountMap', {});

  if (MULTICURRENCY_PRODUCT_TYPE.includes(productCode) && !isSenderIDR) return getEWalletDetail();

  return !isNull(favouriteIndex)
    ? dispatch(getEWalletFavouriteDetail({ userId, favIndex: favouriteIndex, favAccountMaps }))
    : dispatch(getEWalletDetail({ userId }));
};
